import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import Select from 'react-select'

import { httpOk, GET, POST, PUT, FILE, resolveUploadUrl } from 'helpers/api'
import ThemePicker, { defaultTheme } from 'pages/parts/ThemePicker'
import ChannelPosesMenu, { saveChannelPosesMenu } from 'pages/parts/ChannelPosesMenu'
import PageTabs from 'pages/parts/PageTabs'

const UPLOAD_PATH = 'self-orders'

const defaultData = {
	name: 'Самозаказ самообслуживания',
	brand_name: '',
	logo: '',
	attract_image: '',
	theme_id: null,
	colors: null,
	themes: [],
	default_language_id: null,
	external_menu_id: null,
	order_type_hall: true,
	order_type_takeaway: true,
	payment_qr: true,
	payment_terminal: false,
	payment_type_id: null,
	print_receipt: true,
	idle_seconds: 90,
	payment_wait_seconds: 300,
	is_active: true,
	languages: [],
	poses: [],
}

/**
 * Конструктор самозаказа самообслуживания.
 *
 * Тот же каркас, что у конструктора сайта и мобильного приложения: тема берётся
 * из общего каталога `themes` ссылкой, а не копией палитры.
 *
 * Оплаты на кассе тут сознательно нет: самозаказ существует ради того, чтобы гость
 * не шёл к кассиру, поэтому выбор способов — только QR и терминал.
 */
function SelfOrderBuilder() {
	const { t } = useTranslation()

	const [activeTab, setActiveTab] = useState('main')
	const [data, setData] = useState(defaultData)
	const [poses, setPoses] = useState([])
	const [languages, setLanguages] = useState([])
	const [paymentTypes, setPaymentTypes] = useState([])
	const [externalMenus, setExternalMenus] = useState([])
	const [priceCategories, setPriceCategories] = useState([])
	const [posMenus, setPosMenus] = useState({})

	async function load() {
		const response = await GET('/chain/self-order', {}, { loader: true })

		if (httpOk(response)) {
			const payload = {
				...defaultData,
				...response.data,
			}

			if (!payload.theme_id && payload.themes?.length) {
				const theme = defaultTheme(payload.themes)
				payload.theme_id = theme?.id ?? null
				payload.colors = theme?.colors ?? null
			}

			setData(payload)
		}
	}

	async function loadHelpers() {
		const [posesResponse, languagesResponse, paymentTypesResponse, externalMenusResponse, priceCategoriesResponse] = await Promise.all([
			GET('/helper/poses'),
			GET('/helper/languages'),
			GET('/helper/payment-types'),
			GET('/helper/external-menu'),
			GET('/price-categories'),
		])

		if (httpOk(posesResponse)) setPoses(posesResponse.data)
		if (httpOk(languagesResponse)) setLanguages(languagesResponse.data)
		if (httpOk(paymentTypesResponse)) setPaymentTypes(paymentTypesResponse.data)
		if (httpOk(externalMenusResponse)) setExternalMenus(externalMenusResponse.data)
		if (httpOk(priceCategoriesResponse)) setPriceCategories(priceCategoriesResponse.data?.data ?? [])
	}

	async function save(e) {
		e.preventDefault()

		const payload = {
			...data,
			language_id: data.default_language_id ?? data.language_id ?? null,
		}

		const response = data.id
			? await PUT('/chain/self-order', payload, { loader: true, notification: true })
			: await POST('/chain/self-order', payload, { loader: true, notification: true })

		if (httpOk(response)) {
			await saveChannelPosesMenu(posMenus)
			load()
		}
	}

	function change(key, value) {
		setData(prev => ({ ...prev, [key]: value }))
	}

	function changeTheme(option) {
		setData(prev => ({
			...prev,
			theme_id: option?.id ?? null,
			colors: option?.colors ?? null,
		}))
	}

	function changePosStatus(posId, checked) {
		const currentPoses = data.poses?.length ? data.poses : poses

		setData(prev => ({
			...prev,
			poses: currentPoses.map(item => (
				Number(item.id) === Number(posId) ? { ...item, status: checked } : item
			)),
		}))
	}

	function changeLanguages(options) {
		change('languages', options ?? [])
	}

	async function uploadImageFile(file, target) {
		if (!file) return

		const formData = new FormData()
		formData.append('image', file)
		formData.append('path', UPLOAD_PATH)

		const response = await FILE('/upload-image-only', formData, { loader: true })
		if (!httpOk(response)) return

		change(target, response.data.url)
	}

	async function uploadImage(e, target) {
		await uploadImageFile(e.target.files?.[0], target)
		e.target.value = ''
	}

	async function dropImage(e, target) {
		e.preventDefault()
		await uploadImageFile(e.dataTransfer.files?.[0], target)
	}

	function uploadUrl(path) {
		return resolveUploadUrl(path, UPLOAD_PATH)
	}

	useEffect(() => {
		load()
		loadHelpers()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const activePoses = data.poses?.length ? data.poses : poses
	const menuOptions = [
		{ id: null, name: t('main_menu') },
		...externalMenus,
	]
	const selectedMenuOption = data.external_menu_id
		? menuOptions.find(option => Number(option.id) === Number(data.external_menu_id))
		: menuOptions[0]
	const selectedPaymentType = data.payment_type_id
		? paymentTypes.find(option => Number(option.id) === Number(data.payment_type_id))
		: null
	const selectedLanguage = data.default_language_id
		? languages.find(option => Number(option.id) === Number(data.default_language_id))
		: null

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">{t('self_order_constructor')}</h4>
				<button className="btn btn-primary" onClick={save}>{t('save')}</button>
			</div>

			{/* Вкладки, а не одна длинная форма: оформление и список филиалов настраивают
				редко, а основные поля — постоянно, и они тонули между темой и таблицей точек. */}
			<PageTabs
				active={activeTab}
				onChange={setActiveTab}
				tabs={[
					{ key: 'main', label: t('main_settings') },
					{ key: 'theme', label: t('theme') },
					{ key: 'banners', label: t('banners') },
					{ key: 'poses', label: t('poses') },
				]} />

			<form className="page-compact" onSubmit={save} autoComplete="off">

				{activeTab === 'main' &&
					<>
						<div className="card self-order-card shadow-sm mb-3">
							<div className="card-body">
								<div className="row">
									<div className="col-lg-3 form-group">
										<label>{t('name2')}<span className="required-mark">*</span></label>
										<input
											className="form-control"
											value={data.name ?? ''}
											onChange={e => change('name', e.target.value)} />
									</div>
									<div className="col-lg-3 form-group">
										<label>{t('brand_name')}</label>
										<input
											className="form-control"
											value={data.brand_name ?? ''}
											onChange={e => change('brand_name', e.target.value)} />
									</div>
									<div className="col-lg-3 form-group d-flex flex-column">
										<label className="flex-grow-1">{t('default_language')}</label>
										<Select
											options={languages}
											value={selectedLanguage || null}
											onChange={option => change('default_language_id', option?.id ?? null)}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
									<div className="col-lg-3 form-group d-flex flex-column">
										<label className="flex-grow-1">{t('languages')}</label>
										<Select
											isMulti
											options={languages}
											value={data.languages ?? []}
											onChange={changeLanguages}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder=""
										/>
									</div>
								</div>

								<div className="row">
									<div className="col-lg-3 form-group mb-0 d-flex flex-column">
										<label className="flex-grow-1">{t('menu')}</label>
										<Select
											options={menuOptions}
											value={selectedMenuOption || null}
											onChange={option => change('external_menu_id', option?.id ?? null)}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id ?? 'main'}
											placeholder=""
										/>
									</div>
									<div className="col-lg-3 form-group mb-0 d-flex flex-column">
										<label className="flex-grow-1">{t('price_category')}</label>
										<Select
											isClearable
											options={priceCategories}
											value={priceCategories.find(option => option.id === data.price_category_id) || ''}
											onChange={option => change('price_category_id', option ? option.id : null)}
											getOptionLabel={option => option.name}
											getOptionValue={option => option.id}
											placeholder={t('base_price')}
										/>
									</div>
									<div className="col-lg-6 d-flex align-items-end">
										<div className="form-check form-switch form-switch-lg mb-2">
											<input
												type="checkbox"
												className="form-check-input"
												checked={!!data.is_active}
												onChange={e => change('is_active', e.target.checked)} />
											<label className="form-check-label">{t('active')}</label>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className="row">
							<div className="col-lg-6">
								<div className="card self-order-card h-100 shadow-sm">
									<div className="card-body">
										<h6 className="mb-3">{t('self_order_order_types')}</h6>

										<div className="form-check form-switch form-switch-lg mb-2">
											<input
												type="checkbox"
												className="form-check-input"
												checked={!!data.order_type_hall}
												onChange={e => change('order_type_hall', e.target.checked)} />
											<label className="form-check-label">{t('self_order_type_hall')}</label>
										</div>

										<div className="form-check form-switch form-switch-lg mb-4">
											<input
												type="checkbox"
												className="form-check-input"
												checked={!!data.order_type_takeaway}
												onChange={e => change('order_type_takeaway', e.target.checked)} />
											<label className="form-check-label">{t('self_order_type_takeaway')}</label>
										</div>

										<div className="form-check form-switch form-switch-lg mb-4">
											<input
												type="checkbox"
												className="form-check-input"
												checked={!!data.print_receipt}
												onChange={e => change('print_receipt', e.target.checked)} />
											<label className="form-check-label">{t('self_order_print_receipt')}</label>
										</div>

										<div className="row">
											<div className="col-sm-6 form-group mb-0">
												<label>{t('self_order_idle_seconds')}</label>
												<input
													type="number"
													min="30"
													max="600"
													className="form-control"
													value={data.idle_seconds ?? 90}
													onChange={e => change('idle_seconds', Number(e.target.value))} />
											</div>
											<div className="col-sm-6 form-group mb-0">
												<label>{t('self_order_payment_wait_seconds')}</label>
												<input
													type="number"
													min="60"
													max="900"
													className="form-control"
													value={data.payment_wait_seconds ?? 300}
													onChange={e => change('payment_wait_seconds', Number(e.target.value))} />
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="col-lg-6">
								<div className="card self-order-card h-100 shadow-sm">
									<div className="card-body">
										<h6 className="mb-3">{t('payment')}</h6>

										<div className="form-check form-switch form-switch-lg mb-2">
											<input
												type="checkbox"
												className="form-check-input"
												checked={!!data.payment_qr}
												onChange={e => change('payment_qr', e.target.checked)} />
											<label className="form-check-label">{t('self_order_payment_qr')}</label>
										</div>

										<div className="form-check form-switch form-switch-lg mb-4">
											<input
												type="checkbox"
												className="form-check-input"
												checked={!!data.payment_terminal}
												onChange={e => change('payment_terminal', e.target.checked)} />
											<label className="form-check-label">{t('self_order_payment_terminal')}</label>
										</div>

										<div className="form-group mb-0 d-flex flex-column">
											<label className="flex-grow-1">{t('payment_type')}<span className="required-mark">*</span></label>
											<Select
												options={paymentTypes}
												value={selectedPaymentType || null}
												onChange={option => change('payment_type_id', option?.id ?? null)}
												getOptionLabel={option => option.name}
												getOptionValue={option => option.id}
												placeholder=""
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				}

				{activeTab === 'theme' &&
					<div className="card self-order-card shadow-sm">
						<div className="card-body">
							<div className="row">
								<div className="col-lg-4">
									<div className="form-group mb-0">
										<label>{t('logo')}</label>
										<div className="border rounded overflow-hidden bg-light">
											<label
												className="d-flex align-items-center justify-content-center mb-0 cursor"
												onDragOver={e => e.preventDefault()}
												onDrop={e => dropImage(e, 'logo')}
												style={{
													minHeight: 150,
													background: data.logo ? `url(${uploadUrl(data.logo)}) center / contain no-repeat` : '#f8f9fa',
												}}>
												{!data.logo &&
													<div className="text-center text-muted px-2">
														<i className="fa fa-image d-block mb-2" style={{ fontSize: 24 }} />
														<span>{t('upload')}</span>
													</div>
												}
												<input
													type="file"
													accept="image/*"
													className="d-none"
													onChange={e => uploadImage(e, 'logo')} />
											</label>
										</div>
									</div>
								</div>

								<div className="col-lg-8">
									<ThemePicker
										themes={data.themes ?? []}
										value={data.theme_id}
										onChange={changeTheme}
										required
									/>
								</div>
							</div>
						</div>
					</div>
				}

				{/* Баннеры общие на бренд: грузятся один раз в «Бренд → Баннеры», киоск отмечается галочкой */}
				{activeTab === 'banners' &&
					<div className="card self-order-card shadow-sm">
						<div className="card-body">
							<p className="mb-3">{t('brand_banners_moved')}</p>
							<Link to="/brand" className="btn btn-primary">{t('brand_banners_open')}</Link>
						</div>
					</div>
				}

				{activeTab === 'poses' &&
					<div className="card self-order-card shadow-sm">
						<div className="card-body">
							{/* Точки по умолчанию у киоска нет: включённая строка в списке —
								единственный источник правды, отдельное поле его дублировало. */}
							<ChannelPosesMenu
								poses={activePoses}
								overrides={posMenus}
								setOverrides={setPosMenus}
								onToggle={(e, index) => changePosStatus(activePoses[index].id, e.target.checked)} />
						</div>
					</div>
				}

			</form>
		</>
	)
}

export default SelfOrderBuilder
